// The `users` feature's data adapters (Frontend §5.4): the same component tree on #31–#35 (clinic app, C-13) and
// #47–#51 (admin app, the S-04 Users tab and S-05). Same requests, responses and errors on both (API.md §7.11).
import {
  getAdminListUsersQueryKey,
  useAdminAddUser,
  useAdminChangeUserRole,
  useAdminListUsers,
  useAdminRemoveUser,
  useAdminResetUserPassword,
} from '@pa/api/admin'
import {
  getListUsersQueryKey,
  useAddUser,
  useChangeUserRole,
  useListUsers,
  useRemoveUser,
  useResetUserPassword,
  type AddUserRequest,
  type ClinicRole,
  type TempPasswordResult,
  type UserList,
  type UserRow,
} from '@pa/api/clinic'

/** Frontend §3.3: #31 / #47 are fresh for 60 s. */
export const USERS_STALE_MS = 60_000

/** What the components read from a query (both apps' generated hooks satisfy it). */
export interface UsersQuery {
  data: UserList | undefined
  error: unknown
  isPending: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => Promise<unknown>
}

/** A mutation as the components use it: `reset()` drops the result (and a temporary password) from the cache. */
export interface UsersMutation<V, R> {
  mutateAsync: (variables: V) => Promise<R>
  reset: () => void
  isPending: boolean
}

export interface UsersAdapter {
  /** C-13a (clinic app): the role defaults to User; S-05 (admin app): to Admin (UserFlows S-05). */
  defaultRole: ClinicRole
  /** The list's query key, invalidated after every write. */
  listKey: readonly unknown[]
  useList: () => UsersQuery
  useAdd: () => UsersMutation<AddUserRequest, TempPasswordResult>
  useChangeRole: () => UsersMutation<{ userId: string; role: ClinicRole }, UserRow>
  useResetPassword: () => UsersMutation<{ userId: string }, TempPasswordResult>
  useRemove: () => UsersMutation<{ userId: string }, void>
}

/**
 * Frontend §6.5: the mutations that return a temporary password use `gcTime: 0`, and the dialog calls `reset()` on
 * close, so the password never stays in the TanStack mutation cache.
 */
const SECRET = { mutation: { gcTime: 0 } } as const

function wrap<V, R>(
  m: { reset: () => void; isPending: boolean },
  run: (variables: V) => Promise<R>,
): UsersMutation<V, R> {
  return { mutateAsync: run, reset: m.reset, isPending: m.isPending }
}

/** C-13 (clinic app): #31–#35. */
export function clinicUsersAdapter(clinicId: string): UsersAdapter {
  return {
    defaultRole: 'user',
    listKey: getListUsersQueryKey(clinicId),
    useList: () => useListUsers(clinicId, { query: { staleTime: USERS_STALE_MS } }),
    useAdd: () => {
      const m = useAddUser(SECRET)
      return wrap(m, (data: AddUserRequest) => m.mutateAsync({ clinicId, data }))
    },
    useChangeRole: () => {
      const m = useChangeUserRole()
      return wrap(m, ({ userId, role }: { userId: string; role: ClinicRole }) =>
        m.mutateAsync({ clinicId, userId, data: { role } }),
      )
    },
    useResetPassword: () => {
      const m = useResetUserPassword(SECRET)
      return wrap(m, ({ userId }: { userId: string }) => m.mutateAsync({ clinicId, userId }))
    },
    useRemove: () => {
      const m = useRemoveUser()
      return wrap(m, ({ userId }: { userId: string }) => m.mutateAsync({ clinicId, userId }))
    },
  }
}

/** The S-04 Users tab and S-05 (admin app): #47–#51. */
export function adminUsersAdapter(clinicId: string): UsersAdapter {
  return {
    defaultRole: 'admin',
    listKey: getAdminListUsersQueryKey(clinicId),
    useList: () => useAdminListUsers(clinicId, { query: { staleTime: USERS_STALE_MS } }),
    useAdd: () => {
      const m = useAdminAddUser(SECRET)
      return wrap(m, (data: AddUserRequest) => m.mutateAsync({ clinicId, data }))
    },
    useChangeRole: () => {
      const m = useAdminChangeUserRole()
      return wrap(m, ({ userId, role }: { userId: string; role: ClinicRole }) =>
        m.mutateAsync({ clinicId, userId, data: { role } }),
      )
    },
    useResetPassword: () => {
      const m = useAdminResetUserPassword(SECRET)
      return wrap(m, ({ userId }: { userId: string }) => m.mutateAsync({ clinicId, userId }))
    },
    useRemove: () => {
      const m = useAdminRemoveUser()
      return wrap(m, ({ userId }: { userId: string }) => m.mutateAsync({ clinicId, userId }))
    },
  }
}
